import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Budget } from '../types/budget';
import { useUpdateBudget } from '../hooks/useBudgetQueries';

interface BudgetTitleProps {
  budget: Budget;
}

export const BudgetTitle: React.FC<BudgetTitleProps> = ({ budget }) => {
  const { t } = useTranslation();
  const updateBudgetMutation = useUpdateBudget();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(budget.name);

  const handleTitleEdit = () => {
    setTitle(budget.name);
    setIsEditing(true);
  };

  const handleTitleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (title.trim()) {
      const updatedBudget = {
        ...budget,
        name: title.trim(),
      };
      updateBudgetMutation.mutate(updatedBudget);
      setIsEditing(false);
    }
  };

  return (
    <div className="flex items-center">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
        {budget.name}
      </h1>
      <button
        onClick={handleTitleEdit}
        className="ml-2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:hover:bg-gray-700"
      >
        <PencilIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50"
            aria-hidden="true"
          />

          <div className="relative mx-4 w-full max-w-md rounded-lg bg-white p-8 dark:bg-gray-800">
            <Dialog.Title className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
              {t('budget.edit')}
            </Dialog.Title>

            <form onSubmit={handleTitleSave} className="space-y-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
                placeholder={t('budget.name')}
              />
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
